import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  GraduationCap, Sparkles, BookOpen, Users, Mic, Heart, Globe2, Clock, Star, CalendarCheck, ShieldCheck, ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSeo, breadcrumbSchema, courseSchema, faqSchema, orgSchema, howToSchema, speakableSchema, localBusinessSchema } from "@/lib/seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { FaqAccordion } from "@/components/FaqAccordion";
import { FeesChart } from "@/components/FeesChart";
import { LeadFormDialog } from "@/components/LeadFormDialog";

const COURSE_TYPES = [
  { icon: BookOpen,   title: "Quran Reading (Nazra)",     desc: "Build clear, confident recitation from the Madani Mushaf — letter by letter, ayah by ayah." },
  { icon: Mic,        title: "Tajweed Classes",            desc: "Pronounce every harf correctly with rules of madd, ghunnah, qalqalah and the makharij of Arabic letters." },
  { icon: Star,       title: "Hifz-ul-Quran Programs",     desc: "Memorise the Quran with a structured plan — daily sabaq, sabqi and manzil revision, weekly tests." },
  { icon: GraduationCap, title: "One-to-One Quran Tutors", desc: "Private 1-on-1 sessions tailored to your pace — perfect for adults restarting their Quran journey." },
  { icon: Heart,      title: "Female Quran Teachers",      desc: "Verified sister teachers for women and girls who prefer learning in a gentle, sister-led environment." },
  { icon: Users,      title: "Quran Classes for Kids",     desc: "Patient, engaging teachers who know how to keep children focused — from Qaida to fluent recitation." },
  { icon: CalendarCheck, title: "Weekend Quran Classes",  desc: "Saturday & Sunday slots for busy families and working adults — no school-night conflicts." },
  { icon: Globe2,     title: "Quran Classes Worldwide",    desc: "Teachers available across UK, USA, Canada, Australia, Europe and the Middle East — any time-zone." },
];

const FAQS = [
  { q: "How do online Quran classes work?",
    a: "You meet your teacher live on a 1-on-1 video call at the day & time you choose. Each session focuses on your current goal — Nazra, Tajweed, Hifz or Tafsir — and your teacher tracks progress between classes." },
  { q: "Are the Quran teachers qualified?",
    a: "Yes. Every teacher on EasyQuran.UK is interviewed, audition-tested and ijazah-verified where claimed. They are fluent in English (and Urdu / Arabic where applicable) and trained to teach beginners through hifz-level students." },
  { q: "Can my child take Quran lessons online?",
    a: "Absolutely. We offer Quran classes for kids from age 4 upward, with teachers who specialise in keeping children focused. Parents can sit in on the first session to feel comfortable." },
  { q: "Do you offer free trial classes?",
    a: "Yes — your first 1-on-1 session is free so you can assess the teacher and the platform before subscribing. No card required to book the trial." },
  { q: "Are female Quran teachers available?",
    a: "Yes. We have verified female teachers for sisters and girls who prefer female instruction. Just mention this in your free-trial request and we'll match you accordingly." },
  { q: "What are the timings? I live outside the UK.",
    a: "Our teachers work across UK, USA, Canada, Australia, Middle East and Europe time-zones. Pick any slot from 24-hour availability when you book." },
  { q: "How much do online Quran classes cost?",
    a: "Plans start from £24 / month (2 days per week, 30-minute classes) for Quran Recitation. See the full fee chart on this page — Hifz, Tajweed, Tafseer and Juz-30 each have their own monthly plans across 5/3/2-day schedules. The first trial class is always free." },
  { q: "Do you teach Tajweed and Tafsir as well as recitation?",
    a: "Yes. We offer separate course tracks: Nazra (basic reading), Tajweed (rules of recitation), Hifz (memorisation) and Tafsir (meaning & context). You can take more than one in parallel." },
];

const CourseCard = ({ icon: Icon, title, desc }) => (
  <div
    className="rounded-2xl border border-accent/30 bg-card p-5 hover:border-accent transition-colors"
    data-testid={`course-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
  >
    <div className="w-10 h-10 rounded-xl bg-primary/10 border border-accent/40 inline-flex items-center justify-center text-primary mb-3">
      <Icon className="w-5 h-5" />
    </div>
    <h3 className="font-display text-lg leading-tight">{title}</h3>
    <p className="text-sm text-foreground/65 mt-1.5 leading-relaxed">{desc}</p>
  </div>
);

const TrustItem = ({ icon: Icon, text }) => (
  <li className="flex items-start gap-2.5">
    <Icon className="w-4 h-4 text-primary mt-0.5 shrink-0" />
    <span className="text-sm text-foreground/80">{text}</span>
  </li>
);

// Auto-open the lead form once per browser session, ~6s after entering the
// page — long enough that the user has had a chance to scan the hero & price
// chart, but soon enough that intent is fresh.
const AUTO_POP_KEY = "eq_lessons_lead_popped";

export default function LessonsPage() {
  useSeo({
    title: "Online Quran Classes — Live Quran Tutors for Kids & Adults | EasyQuran.UK",
    description: "Book live online Quran classes with verified teachers. 1-to-1 Tajweed, Nazra & Hifz lessons for kids and adults. Female Quran teachers available. Free trial class.",
    path: "/lessons",
    keywords: "online quran classes, online quran classes for kids, quran tutor online, learn quran online, tajweed classes online, hifz classes online, female quran teacher online, one to one quran classes, quran classes for adults, weekend quran classes, quran classes fees",
    jsonLd: [
      orgSchema(),
      localBusinessSchema(),
      breadcrumbSchema([{ label: "Online Quran Classes", to: "/lessons" }]),
      faqSchema(FAQS),
      speakableSchema(),
      courseSchema({
        name: "Online Quran Classes (Nazra, Tajweed, Hifz & Tafseer)",
        description: "Live one-to-one Quran lessons with verified male & female teachers — for kids and adults, worldwide.",
        courseCode: "EQUK-QURAN-LIVE",
        slug: "/lessons",
      }),
      howToSchema({
        name: "How to book a free online Quran class with EasyQuran.UK",
        description: "Get matched with a verified male or female Quran teacher and book your first 1-to-1 trial class — free, no credit card required.",
        slug: "/lessons",
        totalTime: "PT3M",
        steps: [
          { name: "Tap 'Book Free Trial'", text: "Tap the 'Book Live Free Class' button anywhere on EasyQuran.UK to open the lead form." },
          { name: "Share your details", text: "Enter your name, country, email and mobile number. The form takes 30 seconds." },
          { name: "Get matched in 12 hours", text: "Our team reviews your request and matches you with a teacher whose timing & style fit yours." },
          { name: "Meet the teacher", text: "Take your first 30-minute trial class live on video — no card, no commitment." },
          { name: "Continue if you love it", text: "If it's a good fit, pick a 2 / 3 / 5-day-per-week monthly plan in your local currency." },
        ],
      }),
    ],
  });

  const [leadOpen, setLeadOpen] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(AUTO_POP_KEY) === "1") return;
    const t = setTimeout(() => {
      setLeadOpen(true);
      sessionStorage.setItem(AUTO_POP_KEY, "1");
    }, 6000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="w-full" data-testid="lessons-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{ background: "radial-gradient(ellipse 80% 60% at 50% 0%, hsla(var(--primary) / 0.18), transparent 60%), radial-gradient(ellipse 60% 50% at 100% 100%, hsla(var(--accent) / 0.15), transparent 60%)" }}
        />
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-8 sm:pt-12 pb-10">
          <Breadcrumbs items={[{ label: "Online Quran Classes", to: "/lessons" }]} className="mb-5" />
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-accent">
            <Sparkles className="w-3.5 h-3.5" /> Live · 1-to-1 · Worldwide
          </div>
          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05]"
            data-testid="lessons-hero-title"
          >
            Online <span className="text-primary">Quran Classes</span> for Kids & Adults
          </h1>
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-foreground/80 leading-relaxed">
            Learn the Quran the way it was always taught — face-to-face with a teacher. Book live one-to-one
            Nazra, Tajweed and Hifz lessons with verified male and female Quran teachers, available across the
            UK, USA, Canada, Australia and worldwide.
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Button
              onClick={() => setLeadOpen(true)}
              className="pill-btn h-12 px-7 bg-primary hover:bg-primary/90 text-base"
              data-testid="lessons-hero-cta"
            >
              Book Free Trial Class <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            <a
              href="#fees-heading"
              className="text-sm text-foreground/70 hover:text-primary underline-offset-4 hover:underline"
              data-testid="lessons-hero-see-fees"
            >
              See full fee chart ↓
            </a>
          </div>

          <ul className="mt-6 grid grid-cols-2 gap-y-2 gap-x-6 max-w-xl">
            <TrustItem icon={ShieldCheck} text="Ijazah-verified teachers" />
            <TrustItem icon={Heart} text="Male & female tutors" />
            <TrustItem icon={Clock} text="24/7 time-zone coverage" />
            <TrustItem icon={Star} text="First trial class — free" />
          </ul>
        </div>
      </section>

      {/* Course types grid */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14" aria-labelledby="course-types">
        <h2 id="course-types" className="font-display text-3xl sm:text-4xl">Choose your Quran learning path</h2>
        <p className="mt-2 max-w-2xl text-foreground/70">
          Whether you&apos;re starting from the Arabic alphabet or memorising the last few Ajzāʾ, we have a course
          (and a teacher) for your stage.
        </p>
        <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="course-grid">
          {COURSE_TYPES.map(c => <CourseCard key={c.title} {...c} />)}
        </div>
      </section>

      {/* Fees chart */}
      <FeesChart onBook={() => setLeadOpen(true)} />

      {/* Why us */}
      <section className="bg-card/40 border-y border-border" aria-labelledby="why-us">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14 grid md:grid-cols-2 gap-10">
          <div>
            <h2 id="why-us" className="font-display text-3xl sm:text-4xl">Why families choose EasyQuran.UK</h2>
            <ul className="mt-5 space-y-4 text-foreground/85">
              <li className="flex items-start gap-3"><ShieldCheck className="w-5 h-5 text-primary mt-0.5 shrink-0" /><span><strong>Verified teachers only.</strong> Every tutor passes a recitation audition and reference check before joining the platform.</span></li>
              <li className="flex items-start gap-3"><Heart className="w-5 h-5 text-primary mt-0.5 shrink-0" /><span><strong>Patient, child-friendly.</strong> Our teachers are trained to engage children from age 4 — short, focused, fun sessions.</span></li>
              <li className="flex items-start gap-3"><Globe2 className="w-5 h-5 text-primary mt-0.5 shrink-0" /><span><strong>Worldwide time-zones.</strong> Whether you&apos;re in London, Toronto, Sydney or Dubai — we have a teacher live in your window.</span></li>
              <li className="flex items-start gap-3"><CalendarCheck className="w-5 h-5 text-primary mt-0.5 shrink-0" /><span><strong>Built around the Quran you read.</strong> Your teacher can pick up exactly where you left off in our Mushaf — no extra setup.</span></li>
            </ul>
          </div>
          <div className="rounded-3xl border border-accent/35 bg-card p-6 sm:p-8 quran-border">
            <p className="font-arabic text-2xl sm:text-3xl text-primary text-center" dir="rtl">خَيْرُكُمْ مَنْ تَعَلَّمَ ٱلْقُرْآنَ وَعَلَّمَهُ</p>
            <p className="text-center text-foreground/70 mt-3 italic">&ldquo;The best of you is the one who learns the Quran and teaches it.&rdquo;</p>
            <p className="text-center text-xs text-foreground/55 mt-1">— Ṣaḥīḥ al-Bukhārī 5027</p>
            <hr className="my-6 border-accent/25" />
            <p className="text-sm text-foreground/80 leading-relaxed">
              Reading is the first step — a teacher is the second. Book a free assessment and we&apos;ll match you
              with the right tutor based on your goals, schedule and preferences.
            </p>
            <Button
              onClick={() => setLeadOpen(true)}
              className="mt-5 pill-btn w-full bg-primary hover:bg-primary/90"
              data-testid="lessons-talk-cta"
            >
              Talk to a Quran Teacher <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* Location callouts */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14" aria-labelledby="locations">
        <h2 id="locations" className="font-display text-3xl sm:text-4xl">Online Quran classes — wherever you are</h2>
        <p className="mt-2 max-w-2xl text-foreground/70">Region-specific pages with timings, payment options and curriculum tailored to your country.</p>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="location-grid">
          {[
            { label: "United Kingdom", to: "/online-quran-classes/uk", flag: "🇬🇧" },
            { label: "United States", to: "/online-quran-classes/usa", flag: "🇺🇸" },
            { label: "Canada", to: "/online-quran-classes/canada", flag: "🇨🇦" },
            { label: "Australia", to: "/online-quran-classes/australia", flag: "🇦🇺" },
          ].map(l => (
            <Link key={l.to} to={l.to} className="rounded-2xl border border-accent/35 bg-card p-5 hover:border-accent transition-colors text-center">
              <p className="text-2xl" aria-hidden>{l.flag}</p>
              <p className="font-display text-base mt-1.5">{l.label}</p>
              <p className="text-xs text-primary mt-1">See classes for {l.label} →</p>
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-10 sm:py-14" aria-labelledby="faq" id="faq">
        <h2 id="faq" className="font-display text-3xl sm:text-4xl">Frequently asked questions</h2>
        <p className="mt-2 text-foreground/70">Quick answers to the questions parents and learners ask the most.</p>
        <div className="mt-7">
          <FaqAccordion items={FAQS} idPrefix="lessons-faq" />
        </div>
      </section>

      {/* Final CTA */}
      <section id="book" className="max-w-4xl mx-auto px-5 sm:px-8 pb-16">
        <div className="rounded-3xl border border-accent/40 bg-primary/[0.06] p-6 sm:p-10 text-center">
          <h2 className="font-display text-2xl sm:text-3xl">Start your free trial Quran class</h2>
          <p className="mt-2 text-foreground/75">No card. No commitment. Just 30 minutes with a verified Quran teacher.</p>
          <Button
            onClick={() => setLeadOpen(true)}
            className="pill-btn mt-5 h-12 px-7 bg-primary hover:bg-primary/90 text-base"
            data-testid="lessons-cta-submit"
          >
            Book Free Trial <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </section>

      <LeadFormDialog open={leadOpen} onOpenChange={setLeadOpen} source="lessons-page" />
    </div>
  );
}
